import EmObject, { defineProperty, computed } from '@ember/object';
import { isNone } from '@ember/utils';
import { copy } from 'ember-copy';

export const CAN_REFINE_ANY_MESSAGE_IN_CAMPAIGN_CONDITION_AFTER_CAMPAIGN_STARTED_TS = 1631059200; // Sep 08 2021 00:00:00 GMT - we don't have full data to segment on before that

export function addError(errors: Record<string, string[]>, field: string, message: string) {
  if (isNone(errors[field])) {
    errors[field] = [];
  }

  errors[field].push(message);
}

// simplified: necessary to avoid type errors when accessing
export interface MainConditionInterface {
  type: string;

  canRefine?: boolean;

  trackedAttributes: readonly string[];
  changedAttributes: string[];

  errors: Record<string, string[]>;

  isDirty: boolean;
  hasErrors: boolean;
  validate: () => boolean;
  duplicate?: () => AbstractCondition;
  copyAllAttributes?: (source: AbstractCondition, target: AbstractCondition) => void;
  toString: () => string;
}

abstract class AbstractCondition extends EmObject implements MainConditionInterface {
  type = 'abstract';
  uiType?: string;

  // The event name string is set when creating/deserializing conditions for
  // event triggered campaigns
  _event = '';
  get event() {
    return this._event;
  }
  set event(value: string) {
    this.set('_event', value);
  }

  get editableCondition() {
    return this as any;
  }

  get canRefine() {
    return true;
  }
  shouldValidate = true;

  trackedAttributes: readonly string[] = [];
  // placeholder for correct typing, overrided in init() defineProperty call:
  get changedAttributes(): string[] {
    return [];
  }

  _children: (MainConditionInterface | null)[] = [];
  // defined as accessors, not property, because child classes override it as such:
  get children(): (MainConditionInterface | null)[] {
    return this._children;
  }
  set children(val: (MainConditionInterface | null)[]) {
    this.set('_children', val);
  }

  _originalChildren: readonly unknown[] = [];
  _originalAttributes: Record<string, unknown> = {};

  campaignFirstStarted?: number | null; // used to allow/disallow refining "Any 'action' in X campaign" conditions in the condition editor - and for nothing else https://github.com/customerio/ui/pull/6683

  abstract get errors(): Record<string, string[]>;

  @computed('changedAttributes.[]', 'children.@each.isDirty')
  get isDirty() {
    let { children } = this;

    let originals = this._originalChildren;

    // Is the number of children different?
    if (originals.length !== children.length) {
      return true;
    }

    // Is any child is a different object from original?
    for (let i = 0; i < originals.length; i++) {
      if (originals[i] !== children[i]) {
        return true;
      }
    }

    // Are any attributes changed, or any children dirty?
    return this.changedAttributes.length > 0 || children.some((condition) => condition?.isDirty);
  }

  @computed('errors', 'children.@each.hasErrors')
  get hasErrors() {
    let { errors, children } = this;

    if (Object.keys(errors).length > 0) {
      return true;
    }

    return children.some((child) => child?.hasErrors);
  }

  @computed('campaignFirstStarted')
  get isRefinable() {
    return (
      this.campaignFirstStarted !== undefined && // campaignFirstStarted has a value, and so the campaign is either:
      (this.campaignFirstStarted === null || // not started yet, OR
        // started when we already had full data for refining:
        this.campaignFirstStarted > CAN_REFINE_ANY_MESSAGE_IN_CAMPAIGN_CONDITION_AFTER_CAMPAIGN_STARTED_TS)
    );
  }

  init() {
    super.init();

    let originals: Record<string, unknown> = {};

    (this.trackedAttributes as (keyof AbstractCondition)[]).forEach((attr) => (originals[attr] = this.get(attr)));
    Object.freeze(originals);

    defineProperty(
      this,
      'changedAttributes',
      // @ts-ignore: using computed like this is not typed correctly
      computed(...this.trackedAttributes, function (this: AbstractCondition) {
        let tracked = this.trackedAttributes as (keyof AbstractCondition)[];

        return tracked.filter((attr) => this.get(attr) !== originals[attr]);
      }),
    );

    this.set('_originalAttributes', originals);
    this.set('_originalChildren', Object.freeze(copy(this.children)));
  }

  validate() {
    this.set('shouldValidate', true);
    this.children.forEach((c) => c?.validate());

    return !this.hasErrors;
  }

  toString() {
    return 'AbstractCondition';
  }
}

export default AbstractCondition;
